import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { fileSchema } from "./files.js";
/** Excel/CSV import and export (docs/specs/imports.md, C-09). */
export const IMPORT_KINDS = ["products", "customers"];
export const IMPORT_MODES = ["skip_bad_rows", "all_or_nothing"];
export const IMPORT_MATCHES = ["create_only", "upsert_by_sku", "upsert_by_phone"];
export const IMPORT_STATUSES = ["validating", "validated", "committing", "completed", "failed"];
export const EXPORT_STATUSES = ["running", "completed", "failed"];
export const SHEET_FORMATS = ["xlsx", "csv"];
export const MAX_IMPORT_ROWS = 10_000;
export const importKindSchema = z.enum(IMPORT_KINDS);
export const sheetFormatSchema = z.enum(SHEET_FORMATS);
/** `POST /v1/imports`: dry-run a file uploaded with purpose `import`. */
export const startImportRequestSchema = z
    .object({
    fileId: z.uuid(),
    kind: importKindSchema,
    mode: z.enum(IMPORT_MODES),
    match: z.enum(IMPORT_MATCHES),
    /** Where the `opening_stock` column is posted (needs `inventory.adjust` there); required when it is used. */
    openingStockLocationId: z.uuid().optional(),
})
    .strict();
/** One product of the dry-run preview. */
export const importPreviewItemSchema = z.object({
    name: z.string(),
    action: z.enum(["create", "update"]),
    variants: z.int(),
    firstRow: z.int(),
});
export const importJobSchema = z.object({
    id: z.uuid(),
    kind: importKindSchema,
    mode: z.enum(IMPORT_MODES),
    match: z.enum(IMPORT_MATCHES),
    status: z.enum(IMPORT_STATUSES),
    /** Why a job failed: `unreadable`, `too_many_rows`, `has_errors` (all or nothing), `cancelled`, `crashed`. */
    failureReason: z.string().nullable(),
    fileName: z.string(),
    totalRows: z.int(),
    errorRows: z.int(),
    productsToCreate: z.int(),
    variantsToUpdate: z.int(),
    created: z.int(),
    updated: z.int(),
    skipped: z.int(),
    openingStockLocationId: z.uuid().nullable(),
    /** Variants that got opening stock, and those skipped because they already had stock there. */
    openingStockPosted: z.int(),
    openingStockSkipped: z.int(),
    preview: z.array(importPreviewItemSchema),
    /** The bad rows with an `error` column, as XLSX. */
    errorReport: fileSchema.nullable(),
    createdAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
});
export const importJobListSchema = z.object({
    data: z.array(importJobSchema),
    nextCursor: z.uuid().nullable(),
});
export const importErrorSchema = z.object({
    id: z.uuid(),
    /** 1-based, as the spreadsheet shows it (the header is row 1). */
    rowNumber: z.int(),
    column: z.string().nullable(),
    code: z.string(),
    message: z.string(),
});
export const importErrorListSchema = z.object({
    data: z.array(importErrorSchema),
    nextCursor: z.uuid().nullable(),
});
const page = {
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
};
export const importListQuerySchema = z.object(page);
export const importErrorListQuerySchema = z.object(page);
export const templateRequestSchema = z.object({ kind: importKindSchema, format: sheetFormatSchema }).strict();
export const generatedFileResponseSchema = z.object({ file: fileSchema });
export const startExportRequestSchema = z
    .object({ kind: importKindSchema, format: sheetFormatSchema })
    .strict();
export const exportJobSchema = z.object({
    id: z.uuid(),
    kind: importKindSchema,
    format: sheetFormatSchema,
    status: z.enum(EXPORT_STATUSES),
    rowCount: z.int(),
    file: fileSchema.nullable(),
    createdAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
});
/** `imports.job.completed|failed`: for a later in-app notification. */
export const importJobFinishedEventSchema = z.object({
    jobId: z.uuid(),
    kind: importKindSchema,
    status: z.enum(["completed", "failed"]),
    created: z.int(),
    updated: z.int(),
    skipped: z.int(),
});
// Endpoints --------------------------------------------------------------------------------------------------
const tag = { tag: "imports" };
const run = { permission: "imports.run" };
export const startImport = defineEndpoint({
    ...tag,
    name: "startImport",
    method: "POST",
    path: "/v1/imports",
    access: run,
    summary: "Check an uploaded spreadsheet (dry run, as a job)",
    body: startImportRequestSchema,
    status: 202,
    response: importJobSchema,
    errors: ["FILE_REJECTED"],
});
export const listImports = defineEndpoint({
    ...tag,
    name: "listImports",
    method: "GET",
    path: "/v1/imports",
    access: run,
    summary: "Import jobs, newest first",
    query: importListQuerySchema,
    status: 200,
    response: importJobListSchema,
});
export const getImport = defineEndpoint({
    ...tag,
    name: "getImport",
    method: "GET",
    path: "/v1/imports/{id}",
    access: run,
    summary: "One import job with its counts and preview",
    params: idParamsSchema,
    status: 200,
    response: importJobSchema,
});
export const listImportErrors = defineEndpoint({
    ...tag,
    name: "listImportErrors",
    method: "GET",
    path: "/v1/imports/{id}/errors",
    access: run,
    summary: "Row errors of an import, in row order",
    params: idParamsSchema,
    query: importErrorListQuerySchema,
    status: 200,
    response: importErrorListSchema,
});
const jobAction = (action, summary) => defineEndpoint({
    ...tag,
    name: `${action}Import`,
    method: "POST",
    path: `/v1/imports/{id}/${action}`,
    access: run,
    summary,
    params: idParamsSchema,
    status: action === "commit" ? 202 : 200,
    response: importJobSchema,
    errors: ["IMPORT_NOT_VALIDATED"],
});
export const commitImport = jobAction("commit", "Apply a checked import (as a job)");
export const cancelImport = jobAction("cancel", "Drop a checked import without applying it");
export const createImportTemplate = defineEndpoint({
    ...tag,
    name: "createImportTemplate",
    method: "POST",
    path: "/v1/imports/templates",
    access: run,
    summary: "A spreadsheet template to fill in",
    body: templateRequestSchema,
    status: 201,
    response: generatedFileResponseSchema,
});
export const startExport = defineEndpoint({
    ...tag,
    name: "startExport",
    method: "POST",
    path: "/v1/exports",
    access: { permission: "exports.run" },
    summary: "Export to a spreadsheet (as a job)",
    body: startExportRequestSchema,
    status: 202,
    response: exportJobSchema,
});
export const getExport = defineEndpoint({
    ...tag,
    name: "getExport",
    method: "GET",
    path: "/v1/exports/{id}",
    access: { permission: "exports.run" },
    summary: "One export job and, once done, its file",
    params: idParamsSchema,
    status: 200,
    response: exportJobSchema,
});
//# sourceMappingURL=imports.js.map